import React, { useCallback, useEffect, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import Lozenge from '@atlaskit/lozenge/lozenge';
import { Text } from '@atlaskit/primitives/compiled';
import { SpotlightDismissControl } from '@atlaskit/spotlight/dismiss-control';
import { SpotlightHeadline } from '@atlaskit/spotlight/headline';
import { token } from '@atlaskit/tokens';

import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';
import { useMessageData } from '@atlassian/post-office-headless-api/hooks/useMessageData';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

// Editor AI's headless Post Office template (post-office/integrated-teams/editor-ai).
const MESSAGE_TEMPLATE_ID = 'editor-ai-content-reviewer-welcome';

const styles = cssMap({
	// Spotlight's `left-start` shifts the popover so a caret 22px from its top meets the target's centre.
	// Centering the pill in a 44px-high box puts our arrow at exactly that offset. (The legacy popper path
	// centres the popover on the target, which agrees.)
	root: {
		display: 'flex',
		alignItems: 'center',
		minHeight: '44px',
	},
	pill: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		paddingBlockStart: token('space.075'),
		paddingBlockEnd: token('space.075'),
		paddingInlineStart: token('space.200'),
		// Matches the block padding so the close button sits evenly from the top, right and bottom edges.
		paddingInlineEnd: token('space.075'),
		borderRadius: token('radius.large'),
		backgroundColor: token('color.background.neutral.bold'),
		boxShadow: token('elevation.shadow.overflow'),
	},
	// Same technique as the spotlight caret: a token-coloured box clipped to a triangle.
	arrow: {
		width: '9px',
		height: '15px',
		flexShrink: 0,
		// Overlaps the pill by a couple of pixels so sub-pixel rounding can't leave a seam between them.
		marginInlineStart: token('space.negative.025'),
		backgroundColor: token('color.background.neutral.bold'),
		clipPath: 'polygon(0 0, 100% 50%, 0 100%)',
	},
});

const i18n = defineMessages({
	title: {
		id: 'object-sidebar-components.review-nudge.welcome.title',
		defaultMessage: 'Suggested edits',
		description:
			'Title of the spotlight pointing at the Review suggestions button in the object sidebar',
	},
	newLozenge: {
		id: 'object-sidebar-components.review-nudge.welcome.new-lozenge',
		defaultMessage: 'New',
		description:
			'Lozenge next to the Suggested edits spotlight title, marking the feature as newly released',
	},
});

type WelcomeNudgeCardProps = {
	dismissMessage: (messageInstanceId: string) => void;
	messageInstanceId: string;
	onShown?: () => void;
	/** Called when the nudge leaves the screen, whether dismissed or unmounted. */
	onHidden?: () => void;
	subscribeToTargetClick?: (listener: () => void) => () => void;
};

/**
 * A compact callout rather than a `SpotlightCard`, which is fixed at 295px wide. It ignores clicks
 * outside it. The close button closes it (and Escape does, while focus is inside it), and clicking the Review button counts as acting
 * on it.
 */
const WelcomeNudgeCard = ({
	dismissMessage,
	messageInstanceId,
	onShown,
	onHidden,
	subscribeToTargetClick,
}: WelcomeNudgeCardProps) => {
	const { formatMessage } = useIntl();
	const { onClosed, onCtaActioned } = usePostOfficeUserInteractions();
	const { PopoverContent } = useChoreographerAssets();
	const hasRespondedRef = useRef(false);

	useEffect(() => {
		onShown?.();
		return () => onHidden?.();
	}, [onShown, onHidden]);

	// The popover, the close button and the Review button can race; only record one response.
	const respond = useCallback(
		(record: () => void) => {
			if (hasRespondedRef.current) {
				return;
			}
			hasRespondedRef.current = true;
			record();
			dismissMessage(messageInstanceId);
		},
		[dismissMessage, messageInstanceId],
	);

	const handleClose = useCallback(
		() => respond(() => void onClosed({ interactionIdentifier: 'close' })),
		[respond, onClosed],
	);

	const handleTargetClick = useCallback(
		() => respond(() => void onCtaActioned({ interactionIdentifier: 'review-button' })),
		[respond, onCtaActioned],
	);

	useEffect(
		() => subscribeToTargetClick?.(handleTargetClick),
		[subscribeToTargetClick, handleTargetClick],
	);

	return (
		<PopoverContent
			dismiss={handleClose}
			isVisible
			offset={[0, 4]}
			placement="left-start"
			testId="review-button-nudge-welcome-card"
			shouldDismissOnClickOutside={false}
		>
			<div css={styles.root}>
				<div css={styles.pill}>
					{/* PopoverContent labels its dialog with the spotlight headline. */}
					<SpotlightHeadline>
						<Text weight="regular" color="color.text.inverse">
							{formatMessage(i18n.title)}
						</Text>
					</SpotlightHeadline>
					<Lozenge appearance="new">{formatMessage(i18n.newLozenge)}</Lozenge>
					<SpotlightDismissControl
						autoFocus={false}
						onClick={handleClose}
						testId="review-button-nudge-welcome-close"
					/>
				</div>
				<div css={styles.arrow} aria-hidden="true" />
			</div>
		</PopoverContent>
	);
};

export type ReviewWelcomeNudgeProps = {
	/** Called once the nudge is on screen. */
	onShown?: () => void;
	/** Called when the nudge leaves the screen, whether dismissed or unmounted. */
	onHidden?: () => void;
	/** Subscribes to clicks on the Review button. */
	subscribeToTargetClick?: (listener: () => void) => () => void;
	/** Rendered instead when there is no welcome message, so another nudge can take the slot. */
	fallback?: React.ReactNode;
};

/**
 * The "Suggested edits" welcome nudge, anchored to the Review suggestions button through the
 * enclosing spotlight `PopoverProvider`/`PopoverTarget` and Post Office placement provider.
 */
export const ReviewWelcomeNudge = ({
	onShown,
	onHidden,
	subscribeToTargetClick,
	fallback,
}: ReviewWelcomeNudgeProps): React.JSX.Element | null => {
	const { message, PostOfficeMessage, dismissMessage } = useMessageData(
		{ messageTemplateId: MESSAGE_TEMPLATE_ID },
		{ dataOutsideWrapper: true },
	);

	if (!message || !PostOfficeMessage) {
		return <>{fallback}</>;
	}

	return (
		<PostOfficeMessage>
			{() => (
				<WelcomeNudgeCard
					dismissMessage={dismissMessage}
					messageInstanceId={message.messageInstanceId}
					onShown={onShown}
					onHidden={onHidden}
					subscribeToTargetClick={subscribeToTargetClick}
				/>
			)}
		</PostOfficeMessage>
	);
};
